import React, { useContext, useState, useMemo, useRef } from 'react';
import { Card, Button, Alert } from 'react-bootstrap';
import { Column, Row, TableInstance } from 'react-table';
import { RouteComponentProps, Link } from 'react-router-dom';
import _ from 'lodash';
import ConfigContext from '../../../contexts/ConfigContext';
import ErrorBlock from '../../common/ErrorBlock';
import Spinner from '../../common/Spinner';
import { getUserReplacements } from '../../../api/userManagementApi';
import { BulkUserItemType, UserManagementItemType } from '../../../api/userManagementApiTypes';
import UserManagementMenu from './UserManagementMenu';
import DataTable from '../../common/datatables/DataTable';
import {
    INITIAL_SELECT_ID,
    DEFAULT_ADMIN_INITIAL_STATE,
    MAX_COUNT,
    EXPORT_DATE_FORMAT,
    CONTRACT_SIGNER_ORIGIN,
    ApproverRoutingStatusType,
} from '../../../utils/Constants';
import useAsyncWithState from '../../../hooks/useAsyncWithState';
import { UserManagementFormType } from './modal/userManagementFormType';
import UserManagementModal from './modal/UserManagementModal';
import {
    formatAmDisplayString,
    renderDateCell,
    formatDate,
    formatExportText,
} from '../../../utils/displayUtils';
import { generateCSV, getTruncatedSubject } from '../../common/listUtils';
import useStateWithSessionStorage from '../../../hooks/useStateWithSessionStorage';
import UserManagementFilter from './UserManagementFilter';
import AddViewerModal from '../../common/modal/AddViewerModal';

export enum UserManagementEditType {
    ADD = 'ADD',
    REPLACE = 'REPLACE',
    REMOVE = 'REMOVE',
}

export interface UserManagementSearchFormType {
    personId: number;
    personName: string;
    amStatuses: string[];
    templateCodes: string[];
    projectRoleIds: number[];
    dateFrom: string | undefined;
    dateTo: string | undefined;
}

const getSelectKey = (userManagementItemItem: BulkUserItemType) =>
    `${userManagementItemItem.amId}-${userManagementItemItem.recordId}-${userManagementItemItem.projectRoleId}`;

const UserManagement = (routerProps: RouteComponentProps) => {
    const config = useContext(ConfigContext);

    const [search, setSearch] = useStateWithSessionStorage('userManagementSearchObject', '{}');
    const [editType, setEditType] = useState<UserManagementEditType | null>(null);
    const [selectedRecords, setSelectedRecords] = useState<Map<string, UserManagementItemType>>(
        new Map(),
    );

    const [refresh, setRefresh] = useState(0);

    const [initialState, setInitialState] = useStateWithSessionStorage(
        'userManagementInitialState',
        JSON.stringify(DEFAULT_ADMIN_INITIAL_STATE),
    );

    const { personId, personName, dateFrom, dateTo, amStatuses, templateCodes, projectRoleIds } =
        JSON.parse(search) as UserManagementSearchFormType;

    const { value, error, loading } = useAsyncWithState(
        () =>
            personId > 0
                ? getUserReplacements(
                      config,
                      Number(personId),
                      {
                          dateFrom,
                          dateTo,
                          amStatuses,
                          templateCodes,
                          projectRoleIds,
                      },
                      MAX_COUNT,
                  )
                : [],
        [] as Array<UserManagementItemType>,
        [refresh, search],
    );

    const tableInstanceRef = useRef<TableInstance<UserManagementItemType> | null>(null);

    const refreshFunc = () => {
        setRefresh(refresh + 1);
        setEditType(null);
        setSelectedRecords(new Map());
    };

    const userManagementItemOptions = (userManagementItem: UserManagementItemType) => (
        <div className="btn-group options-icon">
            <button
                type="button"
                className="btn btn-link p-0"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
            >
                <span className="fas fa-ellipsis-h text-muted" />
            </button>

            <UserManagementMenu
                routerProps={routerProps}
                personId={personId ? Number(personId) : INITIAL_SELECT_ID}
                personName={personName}
                userManagementItem={userManagementItem}
                onSubmit={refreshFunc}
                refresh={() => refreshFunc()}
            />
        </div>
    );

    const userManagementItemRole = (userManagementItem: UserManagementItemType) => {
        if (userManagementItem.recordOrigin === CONTRACT_SIGNER_ORIGIN) {
            return 'Contract Signer';
        }
        return userManagementItem.projectRoleMeaning;
    };

    const userReplacements = value.map((userReplacement) => ({
        ...userReplacement,
        projectRoleMeaning: userManagementItemRole(userReplacement),
    }));

    if (error) {
        return typeof error === 'object' ? (
            <ErrorBlock errorMessage="Network Error!" />
        ) : (
            <ErrorBlock errorMessage={error} />
        );
    }

    const columns = useMemo(
        () => [
            ...(editType
                ? [
                      {
                          id: 'check',
                          accessor: '',
                          width: 50,
                          Header: (table: { page: Row<UserManagementItemType>[] }) =>
                              useMemo(
                                  () => (
                                      <div className="text-center">
                                          <input
                                              className="margin-left-30"
                                              type="checkbox"
                                              checked={table.page.every((row) => {
                                                  if (
                                                      editType !== UserManagementEditType.ADD &&
                                                      !(
                                                          editType ===
                                                              UserManagementEditType.REMOVE &&
                                                          row.original.removable
                                                      ) &&
                                                      !(
                                                          editType ===
                                                              UserManagementEditType.REPLACE &&
                                                          row.original.replaceable
                                                      )
                                                  ) {
                                                      return true;
                                                  }
                                                  return selectedRecords.has(
                                                      getSelectKey(row.original),
                                                  );
                                              })}
                                              onChange={(e) => {
                                                  const selectedRecordsMap =
                                                      _.cloneDeep(selectedRecords);
                                                  table.page.forEach((row) => {
                                                      if (e.target.checked) {
                                                          if (
                                                              editType ===
                                                                  UserManagementEditType.ADD ||
                                                              (editType ===
                                                                  UserManagementEditType.REMOVE &&
                                                                  row.original.removable) ||
                                                              (editType ===
                                                                  UserManagementEditType.REPLACE &&
                                                                  row.original.replaceable)
                                                          ) {
                                                              selectedRecordsMap.set(
                                                                  getSelectKey(row.original),
                                                                  row.original,
                                                              );
                                                          }
                                                      } else {
                                                          selectedRecordsMap.delete(
                                                              getSelectKey(row.original),
                                                          );
                                                      }
                                                  });
                                                  setSelectedRecords(selectedRecordsMap);
                                              }}
                                          />
                                      </div>
                                  ),
                                  [],
                              ),
                          Cell: (cellProps: {
                              row: {
                                  original: UserManagementItemType;
                              };
                          }) =>
                              useMemo(
                                  () =>
                                      editType === UserManagementEditType.ADD ||
                                      (editType === UserManagementEditType.REMOVE &&
                                          cellProps.row.original.removable) ||
                                      (editType === UserManagementEditType.REPLACE &&
                                          cellProps.row.original.replaceable) ? (
                                          <div className="text-center">
                                              <input
                                                  key={`${cellProps.row.original.amId}-${cellProps.row.original.recordId}`}
                                                  className="margin-left-30"
                                                  type="checkbox"
                                                  checked={selectedRecords.has(
                                                      getSelectKey(cellProps.row.original),
                                                  )}
                                                  onChange={(e) => {
                                                      const selectedRecordsMap =
                                                          _.cloneDeep(selectedRecords);
                                                      if (e.target.checked) {
                                                          selectedRecordsMap.set(
                                                              getSelectKey(cellProps.row.original),
                                                              cellProps.row.original,
                                                          );
                                                      } else {
                                                          selectedRecordsMap.delete(
                                                              getSelectKey(cellProps.row.original),
                                                          );
                                                      }

                                                      setSelectedRecords(selectedRecordsMap);
                                                  }}
                                              />
                                          </div>
                                      ) : null,
                                  [],
                              ),
                          disableSortBy: true,
                      },
                  ]
                : []),
            {
                Header: 'AM#',
                accessor: 'amNumber',
                width: 100,
                Cell: (cellProps: { row: { original: UserManagementItemType } }) =>
                    useMemo(
                        () => (
                            <Link
                                to={{
                                    pathname: `/details/${cellProps.row.original.amId}`,
                                    state: {
                                        returnPageName: 'Bulk User Management',
                                        returnPathName: '/admin/userManagement',
                                    },
                                }}
                            >
                                {formatAmDisplayString(
                                    cellProps.row.original.amNumber,
                                    cellProps.row.original.supNumber,
                                )}
                            </Link>
                        ),
                        [],
                    ),
            },
            {
                Header: 'Subject',
                accessor: 'subject',
                Cell: (cellProps: { value: string }) => getTruncatedSubject(cellProps.value),
                width: null,
            },
            {
                Header: 'Role',
                accessor: 'projectRoleMeaning',
                filterDropDown: true,
                width: 300,
                Cell: (cellProps: { row: { original: UserManagementItemType } }) =>
                    useMemo(() => {
                        let routingStatus = '';
                        if (
                            cellProps.row.original.routingStatus ===
                            ApproverRoutingStatusType.APPROVED
                        ) {
                            routingStatus = `(Approved)`;
                        }
                        if (
                            cellProps.row.original.routingStatus ===
                            ApproverRoutingStatusType.SKIPPED
                        ) {
                            routingStatus = `(Skipped)`;
                        }
                        return (
                            <div>
                                {cellProps.row.original.projectRoleMeaning}
                                {routingStatus && (
                                    <>
                                        {' '}
                                        <span className="font-italic font-weight-light">
                                            <small>{routingStatus}</small>
                                        </span>
                                    </>
                                )}
                            </div>
                        );
                    }, []),
            },
            {
                Header: 'Status',
                accessor: 'status',
                width: 200,
            },
            {
                Header: 'Template Code',
                accessor: 'templateCode',
                width: 200,
            },
            {
                Header: 'Creation Date',
                accessor: 'creationDate',
                Cell: renderDateCell,
            },
            {
                id: 'options',
                Header: '',
                className: 'table-cell-pointer overflow-visible',
                Cell: (cellProps: { row: { original: UserManagementItemType } }) =>
                    userManagementItemOptions(cellProps.row.original),
                disableSortBy: true,
                width: 55,
            },
        ],
        [editType, selectedRecords],
    );

    const initialStateObj = JSON.parse(initialState);

    return (
        <div className="container-fluid mt-3">
            <Alert variant="info">
                Please add, replace or remove template approvers & viewers in Oracle EBS as this
                feature is not supported through this interface.
            </Alert>
            <div className="row row-page-header">
                <div className="col-12 d-flex align-items-center mb-2">
                    <h3 className="mr-auto text-weight-normal mb-0">Bulk User Management</h3>
                    {!editType && (
                        <>
                            <Button
                                variant="outline-primary"
                                className="ml-1"
                                disabled={personId <= 0 || userReplacements.length <= 0}
                                onClick={() => {
                                    setEditType(UserManagementEditType.ADD);
                                }}
                            >
                                <i className="fas fa-user-plus mr-2" /> Add Viewers
                            </Button>
                            <Button
                                variant="outline-primary"
                                className="ml-1"
                                disabled={personId <= 0 || userReplacements.length <= 0}
                                onClick={() => {
                                    setEditType(UserManagementEditType.REPLACE);
                                }}
                            >
                                <i className="fas fa-user-friends mr-2" /> Replace Users
                            </Button>
                            <Button
                                variant="outline-danger"
                                className="ml-1"
                                disabled={personId <= 0 || userReplacements.length <= 0}
                                onClick={() => {
                                    setEditType(UserManagementEditType.REMOVE);
                                }}
                            >
                                <i className="fas fa-user-times mr-2" /> Remove Users
                            </Button>
                        </>
                    )}
                    {personId && editType && (
                        <>
                            {editType === UserManagementEditType.ADD && (
                                <AddViewerModal
                                    title="Add Viewer"
                                    toggleButton={
                                        <Button
                                            variant="primary"
                                            disabled={selectedRecords.size <= 0}
                                        >
                                            <i className="fas fa-user-plus mr-2" /> Bulk Add Viewers
                                        </Button>
                                    }
                                    personId={Number(personId)}
                                    bulkSelect
                                    bulkSelectItems={Array.from(selectedRecords.values()).map(
                                        (userManagementItemItem) => ({
                                            amId: userManagementItemItem.amId,
                                            amNumber: userManagementItemItem.amNumber,
                                            supNumber: userManagementItemItem.supNumber,
                                        }),
                                    )}
                                    onSubmit={refreshFunc}
                                />
                            )}
                            {editType === UserManagementEditType.REPLACE && (
                                <UserManagementModal
                                    title="Replace User"
                                    toggleButton={
                                        <Button
                                            variant="primary"
                                            className="ml-1"
                                            disabled={selectedRecords.size <= 0}
                                        >
                                            <i className="fas fa-user-plus mr-2" /> Bulk Replace
                                            Users
                                        </Button>
                                    }
                                    formType={UserManagementFormType.REPLACE}
                                    personId={Number(personId)}
                                    personName={personName}
                                    bulkSelect
                                    bulkSelectItems={Array.from(selectedRecords.values())}
                                    onSubmit={refreshFunc}
                                />
                            )}
                            {editType === UserManagementEditType.REMOVE && (
                                <UserManagementModal
                                    title="Remove User"
                                    toggleButton={
                                        <Button
                                            variant="danger"
                                            className="ml-1"
                                            disabled={selectedRecords.size <= 0}
                                        >
                                            <i className="fas fa-user-times mr-2" /> Bulk Remove
                                            Users
                                        </Button>
                                    }
                                    formType={UserManagementFormType.REMOVE}
                                    personId={Number(personId)}
                                    personName={personName}
                                    bulkSelect
                                    bulkSelectItems={Array.from(selectedRecords.values())}
                                    onSubmit={refreshFunc}
                                />
                            )}
                            <Button
                                variant="outline-secondary"
                                className="ml-1"
                                onClick={() => {
                                    setEditType(null);
                                    setSelectedRecords(new Map());
                                }}
                            >
                                Cancel Bulk Select
                            </Button>
                        </>
                    )}
                    <Button
                        variant="outline-primary"
                        className="ml-1"
                        disabled={loading || userReplacements.length <= 0}
                        onClick={() => {
                            if (tableInstanceRef.current) {
                                const list = tableInstanceRef.current.rows
                                    .map((row) => row.original)
                                    .map((userManagementItem) => ({
                                        'Action Memo #': userManagementItem.amNumber,
                                        'Supplement #': userManagementItem.supNumber,
                                        Subject: formatExportText(userManagementItem.subject),
                                        Role: userManagementItem.projectRoleMeaning,
                                        Status: userManagementItem.status,
                                        'Template Code': userManagementItem.templateCode,
                                        'Creation Date': formatDate(
                                            userManagementItem.creationDate,
                                            EXPORT_DATE_FORMAT,
                                        ),
                                    }));
                                generateCSV(list, `${personName.replace(/[\W_]+/g, '')}.csv`);
                            }
                        }}
                    >
                        <i className="fas fa-file-export" /> Export CSV
                    </Button>
                </div>
            </div>
            <UserManagementFilter
                setSearchObject={(newObject: Object) => {
                    setInitialState(
                        JSON.stringify({
                            ...initialStateObj,
                            pageIndex: 0,
                        }),
                    );
                    setSearch(JSON.stringify(newObject));
                }}
                searchString={search}
                searching={loading}
                onSubmit={refreshFunc}
            />
            <Card className="py-2">
                <Card.Body>
                    {!loading ? (
                        <DataTable<UserManagementItemType>
                            columns={columns as Column<UserManagementItemType>[]}
                            data={userReplacements}
                            initialState={initialStateObj}
                            setInitialState={setInitialState}
                            noGlobalFilter
                            getInstanceCallback={(tableInstance) => {
                                if (tableInstanceRef) {
                                    tableInstanceRef.current = tableInstance;
                                }
                            }}
                        />
                    ) : (
                        <Spinner />
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default UserManagement;
